import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Checkbox, HStack, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Progress, Select, Table, TableContainer,
  Tbody, Td, Text, Th, Thead, Tr, VStack,
} from '@chakra-ui/react';

import { getBackendURL } from './env';
import { getSupabase, supabaseSchema } from './supabaseClient';
import { parseFileData, getSheetRows } from '../utils/patientImport/parseFile';
import { detectHeader } from '../utils/patientImport/detectHeader';
import { matchColumns } from '../utils/patientImport/matchColumns';
import { TARGET_FIELDS } from '../utils/patientImport/fieldSynonyms';
import { commitImport } from '../utils/patientImport/commitImport';
import { runPatientTableJob } from '../utils/patientTableJob';
import { buildScheduleRows } from '../utils/scheduleImport/buildScheduleRows';
import { fetchSchemaPhysician } from '../utils/scheduleImport/scheduleDefaults';
import {
  fetchExistingAppointments, partitionDuplicates,
} from '../utils/scheduleImport/duplicateAppointments';
import { sendPatientOutreach } from '../utils/scheduleImport/sendPatientOutreach';

const NAME_FORMAT = 'Last, First Middle';

// physician_name is deliberately absent: it is never read from the file. An
// imported sheet is always single-schema, so the physician comes from that
// schema's schema_settings row and nothing else. The rest of TARGET_FIELDS —
// patient_name, email, tel_num, home_phone, dob, type, EHR_ID, date_, time_ — stays
// user-overridable in the mapping list.
const SCHEDULE_FIELDS = TARGET_FIELDS.filter((f) => f.key !== 'physician_name');

export default function ScheduleImportModal({
  isOpen, onClose, selectedDate, defaultType = '', onImported,
}) {
  const [screen, setScreen] = useState('pick'); // pick | review | running
  const [error, setError] = useState('');
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [sheetName, setSheetName] = useState('');
  const [rawRows, setRawRows] = useState([]);
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [dataStartRowIndex, setDataStartRowIndex] = useState(1);
  const [columnMap, setColumnMap] = useState({});
  // matchColumns' per-field confidence ('high' exact | 'medium' substring | 'low'
  // fuzzy), plus 'manual' once the user overrides a field themselves.
  const [confidence, setConfidence] = useState({});
  const [physicianName, setPhysicianName] = useState('');
  const [useFallbackDate, setUseFallbackDate] = useState(false);
  // Default OFF on purpose. Ticking this sends a real SMS and a real email to
  // every imported patient — irreversible and outward-facing, so it is opt-in.
  const [sendOutreach, setSendOutreach] = useState(false);
  const [existingKeys, setExistingKeys] = useState(new Set());
  const [progress, setProgress] = useState({ label: '', done: 0, total: 0 });
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setScreen('pick'); setError(''); setWorkbook(null); setSheetNames([]); setSheetName('');
    setRawRows([]); setHeaderRowIndex(0); setDataStartRowIndex(1); setColumnMap({}); setConfidence({});
    setPhysicianName(''); setUseFallbackDate(false); setSendOutreach(false); setExistingKeys(new Set());
    setProgress({ label: '', done: 0, total: 0 }); setResult(null);
  }, []);

  const handleClose = () => {
    // Tell the parent to reload on the way OUT, not the moment the insert lands.
    // SchedulesTable renders this modal inside its !isLoading branch, so kicking
    // off its refetch unmounts us mid-report — the result screen vanished before
    // anyone could read it. Refreshing at close keeps the report on screen and
    // still lands the user on a reloaded schedule.
    if (result && onImported) onImported(result);
    reset();
    onClose();
  };

  const loadSheet = useCallback((wb, name) => {
    const rows = getSheetRows(wb, name);
    const det = detectHeader(rows);
    setRawRows(rows);
    setHeaderRowIndex(det.headerRowIndex);
    setDataStartRowIndex(det.dataStartRowIndex);
    const match = matchColumns(rows[det.headerRowIndex] || []);
    const detected = { ...match.columnMap };
    // matchColumns will happily bind a "Provider"/"Attending"/"Referring MD"
    // header to physician_name. Drop it: the physician is whoever this schema's
    // schema_settings row says it is, not whoever the spreadsheet names.
    delete detected.physician_name;
    setColumnMap(detected);
    setConfidence({ ...match.confidence });
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError('');
    try {
      const buf = await file.arrayBuffer();
      const { sheetNames: names, workbook: wb } = parseFileData(buf);
      const first = names[0] || '';
      setWorkbook(wb); setSheetNames(names); setSheetName(first);
      loadSheet(wb, first);
      setPhysicianName(await fetchSchemaPhysician(supabaseSchema));
      setScreen('review');
    } catch (err) {
      setError(err?.message || 'Could not read that file.');
    }
  };

  // Everything downstream of the mapping recomputes from it, so the preview and
  // the import can never disagree about what will be written.
  const built = useMemo(() => {
    if (!rawRows.length) {
      return { rows: [], heldBack: { missingDate: [], missingNameCount: 0, unparsedTimeCount: 0, unmappedTypeCount: 0 }, typeConversions: [] };
    }
    return buildScheduleRows({
      rawRows,
      dataStartRowIndex,
      columnMap,
      nameFormat: NAME_FORMAT,
      fallbackDate: useFallbackDate ? selectedDate : null,
      physicianName,
      defaultType,
    });
  }, [rawRows, dataStartRowIndex, columnMap, useFallbackDate, selectedDate, physicianName, defaultType]);

  const dates = useMemo(
    () => [...new Set(built.rows.map((r) => r.date_).filter(Boolean))],
    [built.rows],
  );
  // Join the list so the effect keys on its CONTENT; a fresh array each render
  // would otherwise refire the lookup forever.
  const datesKey = dates.join(',');

  // Look up the days this file touches once the mapping settles.
  useEffect(() => {
    let cancelled = false;
    if (screen !== 'review' || !datesKey) return undefined;
    fetchExistingAppointments(datesKey.split(','))
      .then((keys) => { if (!cancelled) setExistingKeys(keys); })
      .catch(() => { if (!cancelled) setExistingKeys(new Set()); });
    return () => { cancelled = true; };
  }, [screen, datesKey]);

  const { toImport, duplicates } = useMemo(
    () => partitionDuplicates(built.rows, existingKeys),
    [built.rows, existingKeys],
  );

  // A look at the actual data, not just the counts: the first few rows exactly
  // as they will be written. Columns with nothing in them are left out so the
  // table stays readable inside the modal.
  const previewRows = useMemo(() => toImport.slice(0, 5), [toImport]);
  const previewFields = useMemo(
    () => SCHEDULE_FIELDS.filter(
      (f) => previewRows.some((r) => r[f.key] != null && r[f.key] !== ''),
    ),
    [previewRows],
  );

  const setField = (key, value) => {
    setColumnMap((prev) => {
      const next = { ...prev };
      if (value === '') delete next[key];
      else next[key] = Number(value);
      return next;
    });
    // A hand-picked column is no longer a guess — don't keep showing the
    // detector's verdict for a choice the user just made.
    setConfidence((prev) => ({ ...prev, [key]: value === '' ? undefined : 'manual' }));
  };

  // What the badge beside each field says. Mapped-but-fuzzy is the case worth
  // a second look: it imported the right column in testing, but "Appt Dt" only
  // reached date_ through a bigram similarity score, not an exact synonym.
  const badgeFor = (key) => {
    if (typeof columnMap[key] !== 'number') return { label: 'not detected', scheme: 'gray' };
    switch (confidence[key]) {
      case 'manual': return { label: 'you chose', scheme: 'blue' };
      case 'high': return { label: 'exact', scheme: 'green' };
      case 'medium': return { label: 'close', scheme: 'yellow' };
      default: return { label: 'guess — check', scheme: 'orange' };
    }
  };

  const doImport = async () => {
    setScreen('running');
    setError('');
    try {
      const client = await getSupabase();
      const { data: sessionData } = await client.auth.getSession();
      const userId = sessionData?.session?.user?.id || null;

      // user_id is stamped here rather than in the builder so the preview never
      // has to hold a session. notes-c's toRecord is pass-through, so the key
      // reaches the insert untouched.
      const rows = toImport.map((r) => (userId ? { ...r, user_id: userId } : r));

      setProgress({ label: 'Creating appointments...', done: 0, total: rows.length });
      const res = await commitImport(rows, {
        target: 'notes-c',
        // Only needed for the outreach step, which sends a patient their forms
        // and wants the new notes-c id so forms_ind gets flipped on the row.
        returnRows: sendOutreach,
        onProgress: (done, total) => setProgress({ label: 'Creating appointments...', done, total }),
      });

      setProgress({ label: 'Updating the patient list...', done: 0, total: 0 });
      let roster = null;
      try {
        roster = await runPatientTableJob({
          backendUrl: getBackendURL(),
          schema: supabaseSchema || 'public',
        });
      } catch (jobErr) {
        // The appointments are already in. Report the roster failure without
        // pretending the import itself failed.
        setError(`Appointments imported, but updating the patient list failed: ${jobErr.message}`);
      }

      // Welcome SMS + intake forms, exactly as a manually-added patient gets.
      // Runs on the DB rows (they carry the id handleFormSending needs) and
      // AFTER the roster build, so a failure here cannot cost the import.
      let outreach = null;
      if (sendOutreach) {
        const targets = res.insertedRows?.length ? res.insertedRows : rows;
        setProgress({ label: 'Sending forms and welcome messages...', done: 0, total: targets.length });
        outreach = await sendPatientOutreach(targets, {
          onProgress: (done, total) => setProgress({
            label: 'Sending forms and welcome messages...', done, total,
          }),
        });
      }

      // Counted off the rows we sent, so the report says which days the schedule
      // actually changed on — the number the user checks against the file.
      const counts = new Map();
      rows.forEach((r) => counts.set(r.date_, (counts.get(r.date_) || 0) + 1));
      const byDate = [...counts.entries()].sort((a, b) => String(a[0]).localeCompare(String(b[0])));

      const summary = {
        inserted: res.inserted,
        byDate,
        errors: res.errors,
        duplicates: duplicates.length,
        heldBack: built.heldBack,
        withEhr: roster?.inserted?.patients_with_ehrid ?? 0,
        withoutEhr: roster?.inserted?.patients_no_ehrid ?? 0,
        outreach,
      };
      setResult(summary);
    } catch (err) {
      setError(err?.message || 'Import failed.');
      setScreen('review');
    }
  };

  const headers = rawRows[headerRowIndex] || [];
  const heldBackCount = built.heldBack.missingDate.length + built.heldBack.missingNameCount;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Import schedule from CSV / Excel</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error && (
            <Alert status="warning" mb={3}><AlertIcon />{error}</Alert>
          )}

          {screen === 'pick' && (
            <VStack align="stretch" spacing={3}>
              <Text>Choose a .csv, .xlsx or .xls file of appointments.</Text>
              <Input
                data-testid="schedule-import-file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFile}
                p={1}
              />
              <Text fontSize="sm" color="gray.600">
                Column names are detected automatically. Date and patient name are required;
                time, date of birth, cell phone, home phone, email, EHR ID and type are used
                when present.
              </Text>
            </VStack>
          )}

          {screen === 'review' && (
            <VStack align="stretch" spacing={4}>
              {sheetNames.length > 1 && (
                <Box>
                  <Text fontWeight="bold" mb={1}>Sheet</Text>
                  <Select
                    value={sheetName}
                    onChange={(e) => { setSheetName(e.target.value); loadSheet(workbook, e.target.value); }}
                  >
                    {sheetNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  </Select>
                </Box>
              )}

              <Box>
                <Text fontWeight="bold" mb={1}>Detected columns</Text>
                <VStack align="stretch" spacing={1}>
                  {SCHEDULE_FIELDS.map((f) => {
                    const badge = badgeFor(f.key);
                    return (
                      <HStack key={f.key} spacing={2}>
                        <Text fontSize="sm" minW="110px">{f.label}</Text>
                        <Badge
                          colorScheme={badge.scheme}
                          fontSize="0.62rem"
                          minW="86px"
                          textAlign="center"
                          data-testid={`confidence-${f.key}`}
                        >
                          {badge.label}
                        </Badge>
                        <Select
                          size="sm"
                          flex="1"
                          aria-label={f.label}
                          value={typeof columnMap[f.key] === 'number' ? String(columnMap[f.key]) : ''}
                          onChange={(e) => setField(f.key, e.target.value)}
                        >
                          <option value="">Not imported</option>
                          {headers.map((h, i) => (
                            <option key={`${f.key}-${i}`} value={String(i)}>
                              {String(h || `Column ${i + 1}`)}
                            </option>
                          ))}
                        </Select>
                      </HStack>
                    );
                  })}
                </VStack>
                {SCHEDULE_FIELDS.some((f) => badgeFor(f.key).scheme === 'orange') && (
                  <Text fontSize="xs" color="orange.600" mt={1}>
                    Some columns were matched by similarity rather than by name — check the
                    orange ones before importing.
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontWeight="bold">
                  {toImport.length} appointments ready to import
                </Text>
                {physicianName ? (
                  <Text fontSize="sm" color="gray.600">
                    Physician set to <b>{physicianName}</b> for all of them, from this
                    schema&apos;s settings.
                  </Text>
                ) : (
                  <Text fontSize="sm" color="orange.600">
                    No physician is configured for this schema, so physician will be left
                    blank. Set <b>physician_name</b> on this schema in Admin settings.
                  </Text>
                )}
                {duplicates.length > 0 && (
                  <Text fontSize="sm" color="gray.600">
                    {duplicates.length} already on the schedule and will be skipped.
                  </Text>
                )}
              </Box>

              {previewRows.length > 0 && previewFields.length > 0 && (
                <Box>
                  <Text fontWeight="bold" mb={1}>
                    Preview &mdash; first {previewRows.length} row(s)
                  </Text>
                  <TableContainer overflowX="auto">
                    <Table size="sm" variant="simple" data-testid="schedule-import-preview">
                      <Thead>
                        <Tr>
                          {previewFields.map((f) => (
                            <Th key={f.key} fontSize="0.62rem">{f.label}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {previewRows.map((r, i) => (
                          // Index key: preview rows are positional and never reordered.
                          // eslint-disable-next-line react/no-array-index-key
                          <Tr key={i}>
                            {previewFields.map((f) => (
                              <Td key={f.key} fontSize="xs" whiteSpace="nowrap">
                                {r[f.key] == null ? '' : String(r[f.key])}
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {built.typeConversions.length > 0 && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" mb={1}>Appointment types converted</Text>
                  <VStack align="stretch" spacing={0.5}>
                    {built.typeConversions.map((c) => (
                      <HStack key={c.from} fontSize="xs" spacing={2}>
                        <Text color="gray.600" flex="1" noOfLines={1}>{c.from}</Text>
                        <Text aria-hidden="true">→</Text>
                        <Badge colorScheme="green">{c.to}</Badge>
                        <Text color="gray.500" minW="52px" textAlign="right">
                          {c.count} row{c.count === 1 ? '' : 's'}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}

              {built.heldBack.unmappedTypeCount > 0 && (
                <Alert status="info">
                  <AlertIcon />
                  <Text fontSize="sm">
                    {built.heldBack.unmappedTypeCount} row(s) have a type we did not
                    recognise — those import exactly as the file wrote them.
                  </Text>
                </Alert>
              )}

              <Box borderWidth="1px" borderRadius="md" p={3}>
                <Checkbox
                  isChecked={sendOutreach}
                  onChange={(e) => setSendOutreach(e.target.checked)}
                >
                  <Text fontSize="sm" fontWeight="bold">
                    Send forms and a welcome message to these {toImport.length} patient
                    {toImport.length === 1 ? '' : 's'}
                  </Text>
                </Checkbox>
                <Text fontSize="xs" color="gray.600" mt={1} ml={6}>
                  The same messages a patient gets when added by hand: a welcome SMS to
                  anyone with a phone number, and intake forms emailed to new patients
                  (types NP and NS only — follow-ups are never sent forms).
                  <b> This sends real messages and cannot be undone.</b>
                </Text>
              </Box>

              {badgeFor('time_').label === 'not detected' && (
                <Alert status="warning">
                  <AlertIcon />
                  <Text fontSize="sm">
                    No time column was detected, so every appointment will import
                    <b> without a time</b>. Pick the file&apos;s time column above if it has one.
                  </Text>
                </Alert>
              )}

              {built.heldBack.unparsedTimeCount > 0 && (
                <Alert status="warning">
                  <AlertIcon />
                  <Text fontSize="sm">
                    {built.heldBack.unparsedTimeCount} row(s) have a time we could not read —
                    those appointments will import without one.
                  </Text>
                </Alert>
              )}

              {/* A phone too short to be a number (< 7 digits) is left off the
                  row rather than imported as junk — say so, don't swallow it. */}
              {[['tel_num', 'cell phone'], ['home_phone', 'home phone']].map(([key, label]) => {
                const n = built.stats?.unparseable?.[key] || 0;
                if (!n) return null;
                return (
                  <Alert key={key} status="warning">
                    <AlertIcon />
                    <Text fontSize="sm">
                      {n} row(s) have a {label} we could not read — those appointments
                      will import without it.
                    </Text>
                  </Alert>
                );
              })}

              {heldBackCount > 0 && (
                <Alert status="warning" flexDirection="column" alignItems="flex-start">
                  <HStack>
                    <AlertIcon />
                    <Text fontWeight="bold">
                      {heldBackCount} row(s) can&apos;t be imported
                    </Text>
                  </HStack>
                  {built.heldBack.missingNameCount > 0 && (
                    <Text fontSize="sm" mt={1}>
                      {built.heldBack.missingNameCount} row(s) have no patient name.
                    </Text>
                  )}
                  {built.heldBack.missingDate.length > 0 && (
                    <>
                      <Text fontSize="sm" mt={1}>
                        {built.heldBack.missingDate.map((r) => r.patient_name).join(', ')}
                        {' '}— no appointment date.
                      </Text>
                      <Button size="xs" mt={2} onClick={() => setUseFallbackDate((v) => !v)}>
                        {useFallbackDate ? 'Leave them out' : `Put them on ${selectedDate}`}
                      </Button>
                    </>
                  )}
                </Alert>
              )}
            </VStack>
          )}

          {screen === 'running' && (
            <VStack align="stretch" spacing={3}>
              {!result && (
                <>
                  <Text>{progress.label}</Text>
                  <Progress
                    size="sm"
                    isIndeterminate={!progress.total}
                    value={progress.total ? (progress.done / progress.total) * 100 : 0}
                  />
                </>
              )}
              {result && (
                <VStack align="stretch" spacing={2}>
                  <Text fontWeight="bold">Imported {result.inserted} appointments.</Text>
                  <Text fontSize="sm">
                    Patient list: <Badge colorScheme="green">{result.withEhr}</Badge> added with an
                    EHR ID, <Badge>{result.withoutEhr}</Badge> without.
                  </Text>
                  {result.byDate?.length > 0 && (
                    <Box>
                      {result.byDate.map(([d, n]) => (
                        <Text key={d} fontSize="sm" data-testid={`imported-on-${d}`}>
                          {d}: <b>{n}</b> appointment{n === 1 ? '' : 's'}
                        </Text>
                      ))}
                    </Box>
                  )}
                  {result.outreach && (
                    <Text fontSize="sm">
                      Outreach: <Badge colorScheme="green">{result.outreach.welcomeSent}</Badge> welcome
                      message(s), <Badge colorScheme="green">{result.outreach.formsSent}</Badge> form
                      email(s) sent
                      {result.outreach.failed > 0 && (
                        <> — <Badge colorScheme="red">{result.outreach.failed}</Badge> failed</>
                      )}.
                    </Text>
                  )}
                  {result.duplicates > 0 && (
                    <Text fontSize="sm" color="gray.600">
                      {result.duplicates} skipped as already scheduled.
                    </Text>
                  )}
                  {result.errors?.length > 0 && (
                    <Alert status="error">
                      <AlertIcon />
                      {result.errors.length} batch(es) failed: {result.errors[0].message}
                    </Alert>
                  )}
                </VStack>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          {screen === 'review' && (
            <Button
              colorScheme="green"
              onClick={doImport}
              isDisabled={toImport.length === 0}
            >
              Import {toImport.length} appointments
            </Button>
          )}
          <Button
            colorScheme={result ? 'green' : undefined}
            variant={result ? 'solid' : 'ghost'}
            ml={2}
            onClick={handleClose}
          >
            {result ? 'Done' : 'Cancel'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
